import React, { useRef, useEffect, useState } from "react";
import { Bold, Highlighter } from "lucide-react";

export default function TextBlockEditor({ block, onChange }) {
    const editorRef = useRef(null);
    const [isBold, setIsBold] = useState(false);
    const [isHighlight, setIsHighlight] = useState(false);

    const toViewableHtml = (text) => {

        if (!text) return "";

        return text
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")

            // restore our markup
            .replace(/&lt;b&gt;/g, "<strong>")
            .replace(/&lt;\/b&gt;/g, "</strong>")

            .replace(
                /&lt;h&gt;([\s\S]*?)&lt;\/h&gt;/g,
                '<span class="text-highlight">$1</span>'
            )

            .replace(/\n/g, "<br>");

    };

    const findClosest = (node, predicate) => {
        let cur = node;
        while (cur) {
            if (predicate(cur)) return cur;
            cur = cur.parentNode;
        }
        return null;
    };

    function toStoredString() {
        return serialize(editorRef.current);
    }

    function serialize(node) {

        let out = "";

        node.childNodes.forEach(child => {

            if (child.nodeType === Node.TEXT_NODE) {
                out += child.textContent;
                return;
            }

            if (child.nodeName === "BR") {
                out += "\n";
                return;
            }

            if (child.nodeName === "DIV" || child.nodeName === "P") {

                out += serialize(child);

                if (!out.endsWith("\n"))
                    out += "\n";

                return;
            }

            if (child.nodeName === "STRONG") {
                out += `<b>${serialize(child)}</b>`;
                return;
            }

            if (
                child.nodeName === "SPAN" &&
                child.classList.contains("text-highlight")
            ) {
                out += `<h>${serialize(child)}</h>`;
                return;
            }

            out += serialize(child);

        });

        return out;
    }

    const isNodeInsideEditor = (node) => {
        return !!(editorRef.current && node && editorRef.current.contains(node));
    };

    const getActiveHighlightSpan = () => {
        const sel = window.getSelection();
        if (!sel || !sel.rangeCount) return null;

        const node = sel.anchorNode;
        if (!isNodeInsideEditor(node)) return null;

        return findClosest(node, (n) => {
            return (
                n.nodeType === 1 &&
                n.tagName === "SPAN" &&
                n.classList.contains("text-highlight")
            );
        });
    };

    const syncStates = () => {
        setIsBold(document.queryCommandState("bold"));
        setIsHighlight(!!getActiveHighlightSpan());
    };

    const commit = () => {
        if (!editorRef.current) return;

        onChange({
            config: {
                ...block.config,
                text: toStoredString(),
            },
        });
    };

    const wrapSelectionWithHighlight = () => {
        const sel = window.getSelection();
        if (!sel || !sel.rangeCount || sel.isCollapsed) return;

        const range = sel.getRangeAt(0);

        // Ensure selection is inside editor
        if (!editorRef.current.contains(range.commonAncestorContainer)) return;

        const span = document.createElement("span");
        span.className = "text-highlight";

        // Extract selection contents and wrap
        const contents = range.extractContents();
        span.appendChild(contents);
        range.insertNode(span);

        // Move caret after inserted span
        sel.removeAllRanges();
        const after = document.createRange();
        after.setStartAfter(span);
        after.collapse(true);
        sel.addRange(after);
    };

    const unwrapHighlightSpan = (span) => {
        if (!span || !span.parentNode) return;

        const parent = span.parentNode;

        // Move children out
        while (span.firstChild) parent.insertBefore(span.firstChild, span);

        // Remove the span itself
        parent.removeChild(span);
    };

    const applyFormat = (command) => {
        if (command === "bold") {
            document.execCommand("bold", false, null);
            commit();
            syncStates();
            return;
        }

        if (command === "highlight") {
            const active = getActiveHighlightSpan();

            // If cursor is inside highlight OR selection intersects a highlight span,
            // we toggle off by unwrapping the nearest highlight span.
            if (active) {
                unwrapHighlightSpan(active);
            } else {
                wrapSelectionWithHighlight();
            }

            commit();
            syncStates();
        }
    };

    useEffect(() => {
        if (!editorRef.current) return;
        const viewable = toViewableHtml(block.config?.text || "");
        if (editorRef.current.innerHTML !== viewable) {
            editorRef.current.innerHTML = viewable;
        }
    }, [block?.id]); // keep it like this to avoid fighting user typing

    return (
        <div className="space-y-3">
            <div className="flex gap-2">
                <button
                    type="button"
                    onMouseDown={(e) => {
                        e.preventDefault();
                        applyFormat("bold");
                    }}
                    className={`p-2 border rounded ${
                        isBold ? "bg-blue-600 text-white" : "bg-white hover:bg-gray-100"
                    }`}
                    title="Bold"
                >
                    <Bold size={16} />
                </button>

                <button
                    type="button"
                    onMouseDown={(e) => {
                        e.preventDefault();
                        applyFormat("highlight");
                    }}
                    className={`p-2 border rounded ${
                        isHighlight ? "bg-yellow-400 text-black" : "bg-white hover:bg-gray-100"
                    }`}
                    title="Highlight"
                >
                    <Highlighter size={16} />
                </button>
            </div>

            <div
                ref={editorRef}
                contentEditable
                className="w-full min-h-[220px] p-3 border rounded-lg bg-gray-50 outline-none"
                onInput={() => {
                    commit();
                    syncStates();
                }}
                onMouseUp={syncStates}
                onKeyUp={syncStates}
                onFocus={syncStates}
                suppressContentEditableWarning
            />
        </div>
    );
}
